/**
 * filingTrackerDataSource.ts — WatermelonDB → FilingTrackerInput bridge.
 *
 * Loads this store's GST filing records and pairs them with the periods to
 * display. The builder decides what each period's status is; this file only
 * fetches and shapes.
 *
 * `gst_filings` holds one row per (period, returnType) once a return has been
 * lodged. A period with no row is not an error — it is simply not filed yet,
 * which is exactly the case the tracker exists to surface.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { GstFiling } from '@/db/models/System';
import { recentPeriods, type FilingTrackerInput, type FilingRecord, type GstReturnType } from './filingTracker';

/** Return types the tracker reports on. Anything else in the table is ignored. */
const TRACKED: readonly string[] = ['GSTR1', 'GSTR3B'];

/** How many months of history the tracker shows by default (a full year). */
export const DEFAULT_PERIOD_COUNT = 12;

export async function loadFilingTracker(
  storeId: string,
  periodCount: number = DEFAULT_PERIOD_COUNT,
  today: Date = new Date(),
): Promise<FilingTrackerInput> {
  const periods = recentPeriods(today, periodCount);

  const rows = await database.get<GstFiling>('gst_filings').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    // Only the periods on screen — a store that has been filing for years
    // should not drag its whole history into memory to render twelve rows.
    Q.where('period', Q.oneOf(periods)),
  ).fetch();

  const filings: FilingRecord[] = rows
    .filter((r) => TRACKED.includes(r.returnType))
    .map((r) => ({
      period: r.period,
      returnType: r.returnType as GstReturnType,
      status: r.status,
      filedAt: r.filedAt ?? null,
      // The schema calls it ack_number (the GSTN acknowledgement); the tracker
      // calls it arn, which is what the portal shows the shopkeeper.
      arn: r.ackNumber ?? null,
    }));

  return { filings, periods, today };
}
